import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { portalApi, type PortalOrder } from '@/api/portal';
import { formatPlatformTime } from '@/utils/platformTime';

const PAGE_SIZE = 10;

function formatMoney(amount: number, currency: string) {
  const symbol = currency === 'USDT' ? '₮' : '¥';
  return `${symbol}${(amount / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusBadge(status: string) {
  const map: Record<string, { cls: string; label: string }> = {
    created: { cls: 'tech-badge-warning', label: '待支付' },
    pending: { cls: 'tech-badge-warning', label: '处理中' },
    paid: { cls: 'tech-badge-success', label: '已支付' },
    failed: { cls: 'tech-badge-error', label: '失败' },
    closed: { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: '已关闭' },
    expired: { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: '已过期' },
  };
  const s = map[status] ?? { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: status };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function currencyBadge(currency: string) {
  if (currency === 'USDT') {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">USDT</span>;
  }
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">CNY</span>;
}

function payTypeLabel(type: string) {
  const map: Record<string, string> = { alipay: '支付宝', wechat: '微信支付', usdt: 'USDT', bank: '银行卡' };
  return map[type] || type;
}

export default function TransactionsPage() {
  const [orders, setOrders] = useState<PortalOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [tradeNo, setTradeNo] = useState('');
  const [outTradeNo, setOutTradeNo] = useState('');
  const [status, setStatus] = useState('');
  const [payType, setPayType] = useState('');
  const [currency, setCurrency] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  const load = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const res = await portalApi.orders({ pageNum: targetPage, pageSize: PAGE_SIZE, tradeNo, outTradeNo, status, payType, currency });
      setOrders(res.orderList || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : '查询失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
  }, [page]);

  useEffect(() => {
    setPage(1);
  }, [status, payType, currency]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-500">查看和管理全部交易流水（CNY + USDT）</p>
          <p className="text-sm text-slate-500 whitespace-nowrap">共 {total} 条记录</p>
        </div>

        <div className="tech-card p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                <input value={tradeNo} onChange={(e) => setTradeNo(e.target.value)} placeholder="搜索平台单号" className="tech-input w-full pl-9 pr-3 py-2 text-sm" />
              </div>
              <input value={outTradeNo} onChange={(e) => setOutTradeNo(e.target.value)} placeholder="搜索商户单号" className="tech-input px-3 py-2 text-sm" />
            </div>
            <div className="flex flex-wrap gap-2">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="tech-input px-3 py-2 text-sm text-slate-300">
                <option value="">全部币种</option>
                <option value="CNY">CNY 法币</option>
                <option value="USDT">USDT</option>
              </select>
              <select value={payType} onChange={(e) => setPayType(e.target.value)} className="tech-input px-3 py-2 text-sm text-slate-300">
                <option value="">全部方式</option>
                <option value="alipay">支付宝</option>
                <option value="wechat">微信支付</option>
                <option value="usdt">USDT</option>
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="tech-input px-3 py-2 text-sm text-slate-300">
                <option value="">全部状态</option>
                <option value="created">待支付</option>
                <option value="pending">处理中</option>
                <option value="paid">已支付</option>
                <option value="failed">失败</option>
                <option value="closed">已关闭</option>
                <option value="expired">已过期</option>
              </select>
              <button onClick={() => { setPage(1); load(1); }} className="tech-btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-sm">
                <i className="ri-search-line" />
                查询
              </button>
            </div>
          </div>
        </div>

        <div className="tech-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/80 bg-slate-800/60">
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">平台单号</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">商户单号</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">币种</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">支付方式</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">标题</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">金额</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">手续费</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">结算金额</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">状态</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {loading ? (
                  <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-500">加载中...</td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-500">没有找到匹配的交易记录</td></tr>
                ) : orders.map((order) => (
                  <tr key={order.id} className="tech-table-row">
                    <td className="px-4 md:px-5 py-3 font-mono text-xs text-blue-300">{order.tradeNo}</td>
                    <td className="px-4 md:px-5 py-3 font-mono text-xs text-slate-300">{order.outTradeNo}</td>
                    <td className="px-4 md:px-5 py-3">{currencyBadge(order.currency || 'CNY')}</td>
                    <td className="px-4 md:px-5 py-3 text-slate-400">{payTypeLabel(order.payType)}</td>
                    <td className="px-4 md:px-5 py-3 text-slate-400 max-w-[180px] truncate">{order.subject}</td>
                    <td className="px-4 md:px-5 py-3 text-green-400 font-medium">{formatMoney(order.amount, order.currency || 'CNY')}</td>
                    <td className="px-4 md:px-5 py-3 text-amber-300 font-medium">{formatMoney(order.fee || 0, order.currency || 'CNY')}</td>
                    <td className="px-4 md:px-5 py-3 text-cyan-300 font-medium">{formatMoney(order.settleAmount || 0, order.currency || 'CNY')}</td>
                    <td className="px-4 md:px-5 py-3">{statusBadge(order.status)}</td>
                    <td className="px-4 md:px-5 py-3 text-slate-500 text-xs hidden md:table-cell">{formatPlatformTime(order.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 md:px-5 py-3 border-t border-slate-700/80 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-500">显示第 {Math.min((page - 1) * PAGE_SIZE + 1, total)} - {Math.min(page * PAGE_SIZE, total)} 条，共 {total} 条</p>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="tech-btn-secondary px-3 py-1.5 text-xs disabled:opacity-40">上一页</button>
              <span className="text-xs text-slate-400">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="tech-btn-secondary px-3 py-1.5 text-xs disabled:opacity-40">下一页</button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
